import type { ModelData } from '@/types/models';
import { formatCurrency } from '@/utils/formatters';

interface PnLTabProps {
  data: ModelData;
}

export function PnLTab({ data }: PnLTabProps) {
  const years = data?.years || [];
  const pnl = data?.pnl || [];

  // Handle case where data is not yet loaded
  if (!years.length || !pnl.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-4xl mb-3">📈</div>
          <p className="text-slate-500">No P&L data available</p>
        </div>
      </div>
    );
  }

  const rows = [
    { key: 'totalRevenue', label: 'Total Revenue', bold: true, indent: 0 },
    { key: 'cogs', label: 'Cost of Goods Sold', bold: false, indent: 1 },
    { key: 'grossProfit', label: 'Gross Profit', bold: true, indent: 0, highlight: true },
    { key: 'operatingExpenses', label: 'Operating Expenses', bold: false, indent: 1 },
    { key: 'ebitda', label: 'EBITDA', bold: true, indent: 0, highlight: true },
    { key: 'totalDA', label: 'Depreciation & Amortization', bold: false, indent: 1 },
    { key: 'ebit', label: 'EBIT (Operating Income)', bold: true, indent: 0 },
    { key: 'interestExpense', label: 'Interest Expense', bold: false, indent: 1 },
    { key: 'ebt', label: 'EBT (Pre-Tax Income)', bold: true, indent: 0 },
    { key: 'incomeTaxes', label: 'Income Taxes', bold: false, indent: 1 },
    { key: 'netIncome', label: 'Net Income', bold: true, indent: 0, highlight: true, primary: true },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-4 font-semibold text-slate-700 bg-slate-50">
              P&L Line Item
            </th>
            {years.map((year) => (
              <th
                key={year}
                className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-50"
              >
                Year {year}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => {
            const isNegative = ['cogs', 'operatingExpenses', 'totalDA', 'interestExpense', 'incomeTaxes'].includes(row.key);
            
            return (
              <tr
                key={row.key}
                className={`hover:bg-slate-50 ${
                  row.primary
                    ? 'bg-primary-50'
                    : row.highlight
                    ? 'bg-slate-50'
                    : ''
                }`}
              >
                <td
                  className={`py-3 px-4 ${row.bold ? 'font-semibold' : ''} ${
                    row.primary ? 'text-primary-900' : 'text-slate-900'
                  }`}
                  style={{ paddingLeft: `${16 + row.indent * 16}px` }}
                >
                  {row.label}
                </td>
                {pnl.map((yearData, i) => {
                  const value = (yearData?.[row.key as keyof typeof yearData] as number) || 0;
                  return (
                    <td
                      key={i}
                      className={`py-3 px-4 text-right ${
                        row.bold ? 'font-semibold' : ''
                      } ${row.primary ? 'text-primary-900' : 'text-slate-700'}`}
                    >
                      {isNegative && value > 0 ? `(${formatCurrency(value)})` : formatCurrency(value)}
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {/* Margins */}
          <tr className="border-t-2 border-slate-300 hover:bg-slate-50">
            <td className="py-3 px-4 font-medium text-slate-700">
              Gross Margin %
            </td>
            {pnl.map((yearData, i) => {
              const totalRevenue = yearData?.totalRevenue || 1;
              const grossProfit = yearData?.grossProfit || 0;
              const margin = (grossProfit / totalRevenue) * 100;
              return (
                <td key={i} className="py-3 px-4 text-right text-slate-600">
                  {isNaN(margin) ? '-' : `${margin.toFixed(1)}%`}
                </td>
              );
            })}
          </tr>
          <tr className="hover:bg-slate-50">
            <td className="py-3 px-4 font-medium text-slate-700">
              EBITDA Margin %
            </td>
            {pnl.map((yearData, i) => {
              const totalRevenue = yearData?.totalRevenue || 1;
              const ebitda = yearData?.ebitda || 0;
              const margin = (ebitda / totalRevenue) * 100;
              return (
                <td key={i} className="py-3 px-4 text-right text-slate-600">
                  {isNaN(margin) ? '-' : `${margin.toFixed(1)}%`}
                </td>
              );
            })}
          </tr>
          <tr className="hover:bg-slate-50">
            <td className="py-3 px-4 font-medium text-slate-700">
              Net Margin %
            </td>
            {pnl.map((yearData, i) => {
              const totalRevenue = yearData?.totalRevenue || 1;
              const netIncome = yearData?.netIncome || 0;
              const margin = (netIncome / totalRevenue) * 100;
              return (
                <td
                  key={i}
                  className={`py-3 px-4 text-right ${
                    margin >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {isNaN(margin) ? '-' : `${margin.toFixed(1)}%`}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
